import React from "react";
import {
  SideNavigation,
  SideNavigationProps,
} from "@amzn/awsui-components-console";
import {
  CARGO_USER_GUIDE_URL,
  IPL_USER_GUIDE_URL,
  ROUTES,
  WC_USER_GUIDE_URL,
} from "src/app/constants";
import { useLocation, useNavigate } from "react-router";
import { RootState } from "src/redux/store";
import { useAppSelector } from "src/redux/hooks";
import { Role } from "src/auth/types/interfaces";
import { LineOfBusiness } from "src/app/types";
import { useFeatureFlag } from "src/app/featureFlags";

const SideMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeHref, setActiveHref] = React.useState(location.pathname || "/");

  //Redux Auth state
  const { user } = useAppSelector((state: RootState) => state.auth);
  const { lineOfBusiness } = useAppSelector((state: RootState) => state.app);

  // Feature flags
  const { value: enableFormStudio } = useFeatureFlag("enableFormStudio");

  const WC_USER_GUIDE: SideNavigationProps.Link = {
    type: "link",
    text: "User Guide",
    href: WC_USER_GUIDE_URL,
    external: true,
  };

  const CARGO_USER_GUIDE: SideNavigationProps.Link = {
    type: "link",
    text: "User Guide",
    href: CARGO_USER_GUIDE_URL,
    external: true,
  };

  const IPL_USER_GUIDE: SideNavigationProps.Link = {
    type: "link",
    text: "User Guide",
    href: IPL_USER_GUIDE_URL,
    external: true,
  };

  const WC_HEADER: SideNavigationProps.Header = {
    href: ROUTES.WC_CLAIM_INTAKE_FORM,
    text: "Workers' Compensation",
  };

  const CARGO_HEADER: SideNavigationProps.Header = {
    href: ROUTES.CARGO_CLAIM_INTAKE_FORM,
    text: "Cargo",
  };

  const IPL_HEADER: SideNavigationProps.Header = {
    href: ROUTES.IPL_CLAIM_INTAKE_FORM,
    text: "International Product Liability",
  };

  const WC_CLAIMS_HISTORY: SideNavigationProps.Item = {
    type: "link",
    text: "Claims History",
    href: ROUTES.CLAIMS_HISTORY,
    external: true,
  };

  const WC_CLAIM_INTAKE_FORM: SideNavigationProps.Item = {
    type: "link",
    text: "Claim Intake Form",
    href: ROUTES.WC_CLAIM_INTAKE_FORM,
  };

  const WC_ADMIN_TOOLS: SideNavigationProps.Item = {
    type: "section",
    text: "Admin Tools",
    items: [
      {
        type: "link",
        text: "Site Configuration",
        href: ROUTES.SITE_CONFIGURATION,
      },
      {
        type: "link",
        text: "Documents Management",
        href: ROUTES.DOCUMENTS_MANAGEMENT,
      },
      ...(enableFormStudio
        ? [
            {
              type: "link" as const,
              text: "Form Studio",
              href: ROUTES.FORM_STUDIO,
            },
          ]
        : []),
    ],
  };
  const DIVIDER: SideNavigationProps.Item = { type: "divider" };

  const WC_RESOURCES: SideNavigationProps.Item = {
    type: "section",
    text: "Resources",
    items: [
      {
        type: "link",
        text: "Documents Hub",
        href: ROUTES.WORKERS_COMPENSATION_DOCUMENTS,
        external: true,
      },
      WC_USER_GUIDE,
    ],
  };

  const CARGO_CLAIMS_HISTORY: SideNavigationProps.Item = {
    type: "link",
    text: "Claims History",
    href: ROUTES.CARGO_CLAIMS_HISTORY,
    external: true,
  };

  const CARGO_CLAIM_INTAKE_FORM: SideNavigationProps.Item = {
    type: "link",
    text: "Claim Intake Form",
    href: ROUTES.CARGO_CLAIM_INTAKE_FORM,
  };

  const CARGO_RESOURCES: SideNavigationProps.Item = {
    type: "section",
    text: "Resources",
    items: [CARGO_USER_GUIDE],
  };

  const IPL_CLAIMS_HISTORY: SideNavigationProps.Item = {
    type: "link",
    text: "Claims History",
    href: ROUTES.IPL_CLAIMS_HISTORY,
    external: true,
  };

  const IPL_CLAIM_INTAKE_FORM: SideNavigationProps.Item = {
    type: "link",
    text: "Claim Intake Form",
    href: ROUTES.IPL_CLAIM_INTAKE_FORM,
  };

  const IPL_RESOURCES: SideNavigationProps.Item = {
    type: "section",
    text: "Resources",
    items: [IPL_USER_GUIDE],
  };

  function getNavItems(): SideNavigationProps.Item[] {
    let navItems = [];
    switch (lineOfBusiness) {
      case LineOfBusiness.WORKERS_COMPENSATION:
        navItems = [WC_CLAIMS_HISTORY, WC_RESOURCES];
        if (user.roles.includes(Role.WC_CLAIM_USER)) {
          navItems = [WC_CLAIM_INTAKE_FORM, WC_CLAIMS_HISTORY, WC_RESOURCES];
        }
        if (user.roles.includes(Role.WC_CLAIM_USER_LIMITED)) {
          navItems = [WC_CLAIM_INTAKE_FORM, WC_RESOURCES];
        }
        if (user.roles.includes(Role.WC_CLAIM_PRODUCT_GROUP)) {
          navItems = [...navItems, DIVIDER, WC_ADMIN_TOOLS];
        }
        return navItems;
      case LineOfBusiness.CARGO:
        navItems = [
          CARGO_CLAIM_INTAKE_FORM,
          CARGO_CLAIMS_HISTORY,
          CARGO_RESOURCES,
        ];
        return navItems;
      case LineOfBusiness.IPL:
        navItems = [IPL_CLAIM_INTAKE_FORM, IPL_CLAIMS_HISTORY, IPL_RESOURCES];
        return navItems;
      default:
        return [];
    }
  }

  function getHeader(): SideNavigationProps.Header {
    switch (lineOfBusiness) {
      case LineOfBusiness.WORKERS_COMPENSATION:
        return WC_HEADER;
      case LineOfBusiness.CARGO:
        return CARGO_HEADER;
      case LineOfBusiness.IPL:
        return IPL_HEADER;
      default:
        return WC_HEADER;
    }
  }
  return (
    <div className="side-menu-wrapper">
      <SideNavigation
        activeHref={activeHref}
        header={getHeader()}
        onFollow={(event: CustomEvent<SideNavigationProps.FollowDetail>) => {
          // Prevent from following the link if user is at that page
          if (activeHref === event.detail.href) {
            event.preventDefault();
            return;
          }
          if (!event.detail.external) {
            event.preventDefault();
            setActiveHref(event.detail.href);
            navigate(event.detail.href);
          }
        }}
        items={getNavItems()}
      />
    </div>
  );
};
export default SideMenu;
